/**
 * WhatsApp Embedded Signup — pure message-event parsing for the
 * `/whatsapp-signup` page (2026-08-25, whatsapp-embedded-signup-page).
 *
 * Meta's popup posts progress back to the opener via `window.postMessage`.
 * The page listens for `message` events and feeds `event.origin` +
 * `event.data` through {@link parseEmbeddedSignupMessage}; everything
 * DOM-free lives here so the parsing is unit-testable (the page itself is
 * smoke-tested, not vitest-tested, per the web-tree convention).
 *
 * Shapes (verified against Meta's live docs 2026-08-25):
 *
 *   Standard ES finish:
 *     {type: "WA_EMBEDDED_SIGNUP", event: "FINISH",
 *      data: {phone_number_id, waba_id, business_id}}
 *   Coexistence finish (the …504 path — carries ONLY the WABA id; the
 *   backend recovers the phone_number_id from the WABA):
 *     {type: "WA_EMBEDDED_SIGNUP",
 *      event: "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
 *      data: {waba_id}, version: 3}
 *   Abandon:
 *     {type: "WA_EMBEDDED_SIGNUP", event: "CANCEL",
 *      data: {current_step}}
 */

export type EmbeddedSignupMessage =
  | {
      readonly kind: "finish";
      /** Which finish variant fired (coexistence carries its own name). */
      readonly event: string;
      readonly wabaId: string;
      readonly phoneNumberId: string | null;
    }
  | { readonly kind: "cancel"; readonly currentStep: string | null }
  | { readonly kind: "ignored" };

/** Every FINISH-family event name the popup can emit. */
const FINISH_EVENTS = new Set([
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
]);

/**
 * Classify one `message` event. Returns `{kind: "ignored"}` for anything
 * that is not a well-formed WA_EMBEDDED_SIGNUP message from a facebook.com
 * origin — including FINISH payloads with no usable `waba_id`, which are
 * ignored rather than surfaced as a broken finish.
 *
 * `data` arrives either as an object or as a JSON string depending on the
 * SDK build; both are accepted.
 */
export function parseEmbeddedSignupMessage(
  origin: string,
  data: unknown,
): EmbeddedSignupMessage {
  if (!originIsFacebook(origin)) return { kind: "ignored" };
  let parsed: unknown = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return { kind: "ignored" };
    }
  }
  if (parsed === null || typeof parsed !== "object") {
    return { kind: "ignored" };
  }
  const obj = parsed as Record<string, unknown>;
  if (obj["type"] !== "WA_EMBEDDED_SIGNUP") return { kind: "ignored" };
  const event = obj["event"];
  if (typeof event !== "string") return { kind: "ignored" };
  const payload =
    obj["data"] !== null && typeof obj["data"] === "object"
      ? (obj["data"] as Record<string, unknown>)
      : {};
  if (FINISH_EVENTS.has(event)) {
    const wabaId = payload["waba_id"];
    if (typeof wabaId !== "string" || !/^\d{1,32}$/.test(wabaId)) {
      return { kind: "ignored" };
    }
    const phoneNumberId = payload["phone_number_id"];
    return {
      kind: "finish",
      event,
      wabaId,
      phoneNumberId:
        typeof phoneNumberId === "string" && /^\d{1,32}$/.test(phoneNumberId)
          ? phoneNumberId
          : null,
    };
  }
  if (event === "CANCEL") {
    const step = payload["current_step"];
    return {
      kind: "cancel",
      currentStep: typeof step === "string" ? step : null,
    };
  }
  return { kind: "ignored" };
}

/**
 * Meta's documented origin check: the popup posts from a facebook.com
 * host (www.facebook.com / web.facebook.com / …). Suffix-match the HOST,
 * not the raw origin string — `https://evilfacebook.com` must fail while
 * `https://web.facebook.com` passes.
 */
export function originIsFacebook(origin: string): boolean {
  let host: string;
  try {
    host = new URL(origin).hostname;
  } catch {
    return false;
  }
  return host === "facebook.com" || host.endsWith(".facebook.com");
}
